/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import _, { capitalize } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as logger from 'common/utils/Logger';
import DrawerWithPortal from 'common/components/drawer/DrawerWithPortal';
import { LoadingSpinner } from '@motili/ui';
import styles from './AdminBulkApprovalSideBar.module.scss';
import { ApprovalReasonContent } from './PhotoRequirementsApprovalModal';
import MotiliButton from '../../../../common/components-v2/buttons/MotiliButton';
import MotiliButtonContainer from '../../../../common/components-v2/buttons/MotiliButtonContainer';
import * as Validator from '../../../../common/utils/Validator';
import Alert from '../../../../common/components/Alert';
import { useWorkOrderPhotoTypesQuery } from '../../../../common/stores/redux/MetaStore/MetaStoreApi';
import RadioGroup from '../../../../common/components/form/RadioGroup';

export default function AdminBulkApprovalSideBar(props) {
    const [busy, setBusy] = useState(false);
    const [approvalType, setApprovalType] = useState('');
    const [manufactureYear, setManufactureYear] = useState('');
    const [reasons, setReasons] = useState([]);
    const [reasonOther, setReasonOther] = useState('');
    const [additionalReasons, setAdditionalReasons] = useState([]);
    const [additionalComments, setAdditionalComments] = useState('');
    const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();

    const { isFromClickedBulkApproval, photoSelected } = props;

    const displayOnlyApplicable = useMemo(
        () =>
            !isFromClickedBulkApproval ||
            !_.some(
                photoSelected,
                photo => !photo?.photoIntegrity?.validIntegrity
            ),
        [isFromClickedBulkApproval, photoSelected]
    );

    useEffect(() => {
        if (!props.isOpen) {
            handleCloseSlidebar();
        }
    }, [props.isOpen]);

    const handleApproval = async () => {
        setBusy(true);
        const approval = isRequiredReason()
            ? {
                  approvalStatusId: approvalType,
                  reasons,
                  additionalReasons: additionalReasons.map(
                      reason => reason.value
                  ),
                  additionalNotes: additionalComments,
                  reasonOther,
                  manufacturerYear: manufactureYear,
              }
            : {
                  approvalStatusId: 'APPROVE',
                  reasons: [],
              };
        try {
            await props.handleBulkApprovalPhotoRequirement(
                props.photoSelected.map(photo => photo.id),
                approval
            );
            setBusy(false);
            handleCloseSlidebar();
        } catch (error) {
            logger.error(error);
            setBusy(false);
            Alert.error(
                `Sorry, issue approval photo requirement: ${error.message}`
            );
        }
    };
    const otherSelected = reasons => {
        if (reasons) {
            return reasons.find(reason => reason === 'OTHER');
        }
        return false;
    };
    const handleReasonsChanged = change => {
        setReasons(reasons => {
            if (!otherSelected(reasons)) {
                setReasonOther('');
            }
            return change;
        });
    };
    const clearReason = () => {
        setManufactureYear('');
        setReasons([]);
        setReasonOther('');
        setAdditionalReasons([]);
        setAdditionalComments('');
    };
    const handleApprovalActionOnChanged = approvalTypeSelect => {
        if (approvalType !== approvalTypeSelect) {
            setApprovalType(approvalTypeSelect);
            clearReason();
        }
    };
    const isRequiredReason = () => {
        if (approvalType === 'APPROVE') {
            return !!props.photoSelected.find(photo => !approveValid(photo));
        }
        return true;
    };
    const approveValid = photo =>
        (!photo.photoPrediction?.classification ||
            photo.photoPrediction?.classification?.validClassification) &&
        (!photo.photoPrediction?.identification ||
            photo.photoPrediction?.identification?.validIdentification) &&
        (!photo.photoIntegrity || photo.photoIntegrity?.validIntegrity);
    const submitDisabled = () => {
        return (
            !approvalType ||
            (isRequiredReason() && reasons.length <= 0) ||
            (otherSelected(reasons) && !reasonOther) ||
            (manufactureYear &&
                !Validator.isManufacturerYear(manufactureYear)) ||
            manufactureYear === 0
        );
    };
    const handleCloseSlidebar = () => {
        setApprovalType('');
        clearReason();
        props.onClose();
    };
    const approvalActions = [
        { id: 'APPROVE', label: 'Approve', display: 'Approve' },
        { id: 'REJECT', label: 'Reject', display: 'Reject' },
        { id: 'ACCEPT', label: 'Accept', display: 'Accept' },
    ];
    const selectedPhotoDisplay = photos => {
        const groupByPhotoType = _.groupBy(photos, 'requirement.photoTypeId');
        const messages = Object.keys(groupByPhotoType).map(photoTypeId => (
            <div className={styles.selectionDisplay}>
                {`${
                    workOrderPhotoTypes.find(t => t.id === photoTypeId)?.display
                } ${
                    groupByPhotoType[photoTypeId].length > 1
                        ? `( ${groupByPhotoType[photoTypeId].length} photos selected )`
                        : ''
                }`}
            </div>
        ));
        return messages;
    };
    return (
        <DrawerWithPortal
            anchor='right'
            open={props.isOpen}
            customDrawerStyles={panelStyle}
            showDrawerOverlay
            customOverlayStyles={overlayStyle}
            showCloseIcon={false}
            title={
                <div>
                    <div>Bulk Approval</div>
                </div>
            }
            container={document.body}
        >
            {busy ? (
                <LoadingSpinner fullscreen />
            ) : (
                <div className={styles.container}>
                    <div className={styles.body}>
                        {displayOnlyApplicable && (
                            <div style={{ marginBottom: '20px' }}>
                                Only photos that qualify for approval can be
                                bulked approved.
                            </div>
                        )}
                        <div>{`You are currently selecting ${
                            props.photoSelected.length
                        } photo${
                            props.photoSelected.length > 1 ? 's' : ''
                        }.`}</div>
                        <div className={styles.selectedList}>
                            {selectedPhotoDisplay(props.photoSelected)}
                        </div>
                        <div className={styles.bulkDropdownActions}>
                            <RadioGroup
                                dataTestId='admin-bulk-approval-sidebar-actions-radio-group'
                                name='actionSelectorRadioGroup'
                                label={
                                    <div style={{ fontSize: '14px' }}>
                                        SELECT BULK ACTION
                                    </div>
                                }
                                value={approvalType}
                                onChange={value => {
                                    handleApprovalActionOnChanged(value);
                                }}
                                options={approvalActions}
                                required
                            />
                        </div>
                        {approvalType && isRequiredReason() && (
                            <>
                                <div className={styles.header}>
                                    {approvalType &&
                                        `Photo ${capitalize(
                                            approvalType
                                        )} Reason`}
                                </div>
                                <ApprovalReasonContent
                                    busy={busy}
                                    approvalType={approvalType}
                                    manufactureYear={manufactureYear}
                                    setManufactureYear={setManufactureYear}
                                    reasonSelected={reasons}
                                    handleReasonsChanged={handleReasonsChanged}
                                    reasonOther={reasonOther}
                                    setReasonOther={setReasonOther}
                                    additionalReasons={additionalReasons}
                                    setAdditionalReasons={setAdditionalReasons}
                                    additionalComments={additionalComments}
                                    setAdditionalComments={
                                        setAdditionalComments
                                    }
                                />
                            </>
                        )}
                    </div>
                    <div className={styles.footer}>
                        <MotiliButtonContainer>
                            <MotiliButton
                                text='Cancel'
                                onClick={handleCloseSlidebar}
                                disabled={false}
                                moStyle='normal'
                            />
                            <MotiliButton
                                text='Submit'
                                onClick={handleApproval}
                                busy={false}
                                disabled={submitDisabled()}
                            />
                        </MotiliButtonContainer>
                    </div>
                </div>
            )}
        </DrawerWithPortal>
    );
}
const panelStyle = {
    overflow: 'auto',
    width: '535px',
    position: 'fixed',
    height: '100%',
    zIndex: 12,
};
const overlayStyle = {
    position: 'fixed',
};
AdminBulkApprovalSideBar.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    photoSelected: PropTypes.array.isRequired,
    onClose: PropTypes.func,
    handleBulkApprovalPhotoRequirement: PropTypes.func.isRequired,
    isFromClickedBulkApproval: PropTypes.bool,
};
